import { forkJoin, iif, Observable, of, ReplaySubject } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

import { MtnaDataType, RelationalOperator } from '@mtna/data-core-ui';
import { instanceOfValueExpression, LogicalComponent, Node, StreamTokenizer, TokenizerUtil, ValueExpression } from '@mtna/data-query-ui';
import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import {
  invertNumericOperation,
  RDS_NUMERIC_OPERATION_OPTIONS,
  RdsNumericFilter,
  RdsNumericOperation,
} from '../../numeric-filter/numeric-filter.model';
import { invertTextOperation, RDS_TEXT_PARAMETER_OPTIONS, RdsTextFilter } from '../../text-filter/text-filter.model';
import { RdsMetadataService } from '../services/rds-metadata.service';
import { RdsDataTypeUtil } from '../utils/data-type-util';
import { _rdsVerifyMomentJsExists } from '../utils/verify-moment-js-util';
import {
  BaseFilter,
  FilterByCodes,
  FilterByDate,
  FilterByNumber,
  FilterByText,
  FilterDisplayType,
  getRdsDateFilterIso,
  instanceOfFilterByCodes,
  instanceOfFilterByDate,
  instanceOfFilterByNumber,
  instanceOfFilterByText,
  invertDateOperation,
  invertNullOperation,
  RDS_DATE_OPERATION_OPTIONS,
  RDS_NULL_PARAMETER_OPTIONS,
  RdsDateFilter,
  RdsDateOperation,
  RdsNullFilter,
  RdsNullParameter,
  RdsNullParameterOption,
} from './filter/index';

import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';

const moment = _rollupMoment || _moment;

interface FilteredVariable {
  variableId: string;
  filters: Array<Filter>;
}

interface Filter {
  value: any;
  operation: RelationalOperator;
}

/**
 * Uses the Tokenizer to desirialize a where clause into a LogicalComponent.
 * @param where RDS where clause
 */
export function deserializeWhereClause(where: string): LogicalComponent {
  const tokenizer = new StreamTokenizer(where);
  tokenizer.resetSyntax();
  tokenizer.wordChars('*', '}');
  tokenizer.wordChars('!', '!');
  tokenizer.wordChars('#', `'`);
  tokenizer.wordChars(128 + 32, 255);
  tokenizer.whitespaceChars(0, ' ');
  tokenizer.commentChar('/');
  tokenizer.quoteChar('"');

  let result: Node = null;
  try {
    tokenizer.nextToken();
    result = TokenizerUtil.parseExpression(tokenizer);
    if (tokenizer.ttype !== StreamTokenizer.TT_EOF) {
      throw new Error(`EOF expected, got ${tokenizer.ttype}/${tokenizer.sval}`);
    }
  } catch {
    throw new Error(`Could not parse expression [${where}]`);
  }
  return result.getLogicalComponent();
}

/**
 * Async method to deserialize a LogicalComponent into the corresponding RDS Filters.
 *
 * @param logicalComponent output of deserializeWhereClause
 * @param metadataService rds service used to query variables and classifications
 */
export function deserializeLogicalComponent(
  logicalComponent: LogicalComponent,
  metadataService: RdsMetadataService
): Observable<BaseFilter[]> {
  const sub = new ReplaySubject<BaseFilter[]>(1);
  const filterMap: { [id: string]: FilteredVariable } = {};

  _extractFilterIds(logicalComponent, filterMap);

  const rdsFilters$: Observable<BaseFilter>[] = [];

  // Loop through all variables being used as filters
  for (const prop in filterMap) {
    if (filterMap.hasOwnProperty(prop)) {
      const vf: FilteredVariable = filterMap[prop];
      rdsFilters$.push(
        // Query the full Variable
        metadataService.getVariable(vf.variableId).pipe(
          concatMap((variable: Variable) =>
            // If it has a classification, query the codes as well
            iif(
              () => !!variable.classificationUri,
              metadataService.getCodes(variable.classificationUri).pipe(map((codes) => ({ variable, codes }))),
              of({ variable, codes: [] })
            )
          ),
          map(({ variable, codes }) => {
            // Null is a reserved filter, handle it separately
            const filters = vf.filters.filter((f) => f.value !== 'NULL');

            // Determine if there is a null filter
            const nullFilter: Filter = vf.filters.find((f) => f.value === 'NULL');
            let rdsNullFilter: RdsNullFilter;
            if (!!nullFilter) {
              const nullParameter: RdsNullParameter = nullFilter.operation === '=' ? '=NULL' : '!=NULL';
              const nullParameterOption: RdsNullParameterOption = RDS_NULL_PARAMETER_OPTIONS.find((o) => o.type === nullParameter);
              rdsNullFilter = new RdsNullFilter(nullParameterOption);
            }

            if (!!variable.classificationUri) {
              // Code filter
              return new FilterByCodes(
                variable,
                filters.map((f) => codes.find((c) => c.codeValue === f.value)),
                rdsNullFilter
              );
            } else if (RdsDataTypeUtil.simplifyMtnaDataType(variable.dataType as MtnaDataType) === 'TEXT') {
              // Text filter
              return new FilterByText(
                variable,
                filters.map(
                  (f) =>
                    new RdsTextFilter(
                      f.value,
                      RDS_TEXT_PARAMETER_OPTIONS.find((o) => o.type === f.operation)
                    )
                ),
                rdsNullFilter
              );
            } else if (RdsDataTypeUtil.simplifyMtnaDataType(variable.dataType as MtnaDataType) === 'NUMERIC') {
              // Numeric filter
              return new FilterByNumber(
                variable,
                filters.map(
                  (f) =>
                    new RdsNumericFilter(
                      f.value,
                      RDS_NUMERIC_OPERATION_OPTIONS.find((o) => o.type === f.operation)
                    )
                ),
                rdsNullFilter
              );
            } else if (variable.dataType === 'DATE') {
              // Ensure moment js was added to the application correctly
              _rdsVerifyMomentJsExists();

              // TODO support TIME and TIMESTAMP
              // Date filter
              return new FilterByDate(
                variable,
                filters.map(
                  (f) =>
                    new RdsDateFilter(
                      moment(f.value),
                      RDS_DATE_OPERATION_OPTIONS.find((o) => o.type === f.operation)
                    )
                ),
                rdsNullFilter
              );
            }
          })
        )
      );
    }
  }

  forkJoin(rdsFilters$).subscribe(
    (baseFilters) => sub.next(baseFilters),
    (err) => sub.error(err),
    () => sub.complete()
  );

  return sub.asObservable();
}

/**
 * Turn an option into a URL parameter.
 * This option must be an array of primative values.
 *
 * @export
 * @param paramName parameter name, option name
 * @param value array of primative values for an option
 * @returns URL parameter
 */
export function serializeArrayToUrlParams(paramName: string, value: Array<string | boolean | number>) {
  return !paramName || !value || !value.length
    ? ''
    : `${paramName}=${value.map((v) => (typeof v === 'string' ? encodeURIComponent(v) : v)).join(',')}&`;
}

/**
 * Generate the RDS api where clause for the select query
 * @param filterState Filter display type
 * @param appliedFilters applied filters
 * @returns where clause
 */
export function serializeWhereClause(filterState: FilterDisplayType, appliedFilters: BaseFilter[]): string {
  return filterState === 'OFF' || !appliedFilters || !appliedFilters.length
    ? null
    : appliedFilters
        .map((bf) => {
          if (instanceOfFilterByCodes(bf)) {
            // For each variable with codes, create a string that joins them, i.e:
            // (STATE=01 OR STATE=02 OR STATE=03) if the filter state is ON or
            // (STATE!=01 AND STATE!=02 AND STATE!=03) if the filter state is INVERTED
            let joinedFilters = bf.codes
              .map((code) => `${bf.variable.id}${filterState === 'ON' ? '=' : '!='}${code.codeValue}`)
              .join(`${filterState === 'ON' ? ' OR ' : ' AND '}`);

            // Add null filter if it exists
            joinedFilters = joinNullFilter(bf, joinedFilters, filterState);
            return `(${joinedFilters})`;
          } else if (instanceOfFilterByNumber(bf)) {
            return `(${_serializeNumericFilters(bf, filterState === 'INVERTED')})`;
          } else if (instanceOfFilterByText(bf)) {
            let joinedFilters = bf.textFilters
              .map((t) => `${bf.variable.id}${filterState === 'ON' ? t.parameter.type : invertTextOperation(t.parameter.type)}${t.text}`)
              .join(`${filterState === 'ON' ? ' OR ' : ' AND '}`);
            // Add null filter if it exists
            joinedFilters = joinNullFilter(bf, joinedFilters, filterState);
            return `(${joinedFilters})`;
          } else if (instanceOfFilterByDate(bf)) {
            return `(${_serializeDateFilters(bf, filterState === 'INVERTED')})`;
          } else {
            throw new Error(`Unknown filter type: ${JSON.stringify(bf)}`);
          }
        })
        // AND join all the variable clauses, i.e.:
        // (STATE=01 OR STATE=02 OR STATE=03) AND (REGION=01 || REGION=02)
        .join(' AND ');
}

/**
 * If a null filter exits, concatentate it on the filter string
 * @returns a new filter string with the null filter
 */
function joinNullFilter(bf: BaseFilter<Variable | VariableSummary>, joinedFilters: string, filterState: 'ON' | 'INVERTED') {
  let updatedJoinedFilters = joinedFilters;
  if (bf.nullFilter && bf.nullFilter.parameter?.type !== 'NOT_APPLIED') {
    if (!!joinedFilters) {
      updatedJoinedFilters += filterState === 'ON' ? ' OR ' : ' AND ';
    }
    updatedJoinedFilters += `${bf.variable.id}${
      filterState === 'ON' ? bf.nullFilter.parameter.type : invertNullOperation(bf.nullFilter.parameter.type)
    }`;
  }
  return updatedJoinedFilters;
}

/**
 * Returns string combination of all date filter parameters
 * @param bf FilterByDate containing all date filters
 * @param invert Whether the filters inverted
 */
function _serializeDateFilters<V extends Variable | VariableSummary>(bf: FilterByDate<V>, invert = false): string {
  const eqArray: Array<RdsDateFilter> = bf.dateFilters.filter((n) => n.operation.type === '=' || n.operation.type === '!=');
  const compareArray: Array<RdsDateFilter> = bf.dateFilters.filter((n) => n.operation.type !== '=' && n.operation.type !== '!=');
  const eqString = eqArray
    .map(
      (n, i, self) =>
        `${bf.variable.id}${!invert ? n.operation.type : invertDateOperation(n.operation.type)}${getRdsDateFilterIso(n)}${
          i < self.length - 1
            ? !invert
              ? _getEqualitySeparator(n.operation.type)
              : _getEqualitySeparator(invertDateOperation(n.operation.type))
            : ''
        }`
    )
    .join('')
    .concat(
      !!eqArray[0] && !!compareArray.length
        ? _getEqualitySeparator(!invert ? eqArray[0].operation.type : invertDateOperation(eqArray[0].operation.type))
        : ''
    );

  const cString = compareArray
    .map((n, i, self) => {
      const hasNext = !!self[i + 1];
      const prefix = i % 2 === 0 && hasNext ? '(' : '';
      let suffix = i % 2 !== 0 ? ')' : '';
      if (i % 2 !== 0 && hasNext) {
        suffix += ' OR ';
      }

      return `${prefix}${bf.variable.id}${!invert ? n.operation.type : invertDateOperation(n.operation.type)}${getRdsDateFilterIso(n)}${
        i % 2 === 0 && hasNext ? _determineSeparator(n, self[i + 1], invert) : ''
      }${suffix}`;
    })
    .join('');

  let joinedFilters = eqString + cString;
  // Add null filter if it exists
  joinedFilters = joinNullFilter(bf, joinedFilters, invert ? 'INVERTED' : 'ON');
  return joinedFilters;
}

/**
 * Returns string combination of all numeric filter parameters
 * @param bf FilterByNumber containing all numeric filters
 * @param invert Whether the filters are normal or inverted
 */
function _serializeNumericFilters<V extends Variable | VariableSummary>(bf: FilterByNumber<V>, invert = false): string {
  const eqArray: Array<RdsNumericFilter> = bf.numericFilters.filter((n) => n.operation.type === '=' || n.operation.type === '!=');
  const compareArray: Array<RdsNumericFilter> = bf.numericFilters.filter((n) => n.operation.type !== '=' && n.operation.type !== '!=');
  const eqString = eqArray
    .map(
      (n, i, self) =>
        `${bf.variable.id}${!invert ? n.operation.type : invertNumericOperation(n.operation.type)}${n.value}${
          i < self.length - 1
            ? !invert
              ? _getEqualitySeparator(n.operation.type)
              : _getEqualitySeparator(invertNumericOperation(n.operation.type))
            : ''
        }`
    )
    .join('')
    .concat(
      !!eqArray[0] && !!compareArray.length
        ? _getEqualitySeparator(!invert ? eqArray[0].operation.type : invertNumericOperation(eqArray[0].operation.type))
        : ''
    );

  const cString = compareArray
    .map((n, i, self) => {
      const hasNext = !!self[i + 1];
      const prefix = i % 2 === 0 && hasNext ? '(' : '';
      let suffix = i % 2 !== 0 ? ')' : '';
      if (i % 2 !== 0 && hasNext) {
        suffix += ' OR ';
      }

      return `${prefix}${bf.variable.id}${!invert ? n.operation.type : invertNumericOperation(n.operation.type)}${n.value}${
        i % 2 === 0 && hasNext ? _determineSeparator(n, self[i + 1], invert) : ''
      }${suffix}`;
    })
    .join('');

  let joinedFilters = eqString + cString;
  // Add null filter if it exists
  joinedFilters = joinNullFilter(bf, joinedFilters, invert ? 'INVERTED' : 'ON');
  return joinedFilters;
}

/**
 * Returns a string separator to insert between two numeric/date filters.
 * @param a RdsNumericFilter | RdsDateFilter on left of separator
 * @param b RdsNumericFilter | RdsDateFilter on right of separator
 * @param invert Whether the filters are inverted
 */
function _determineSeparator(a: RdsNumericFilter | RdsDateFilter, b: RdsNumericFilter | RdsDateFilter, invert = false): string {
  const op1 = invert ? ' OR ' : ' AND ';
  const op2 = invert ? ' AND ' : ' OR ';
  switch ((a.operation.type + b.operation.type).replace(/\=/g, '')) {
    case '><':
      return a.value < b.value ? op1 : op2;
    case '<>':
      return a.value > b.value ? op1 : op2;
  }
}

/**
 * Returns a string separator for equality signs.
 * @param op RdsNumericOperation  associated with a separator; Must be `=` or `!=`
 */
function _getEqualitySeparator(op: RdsNumericOperation | RdsDateOperation): string {
  switch (op) {
    case '=':
      return ` OR `;
    case '!=':
      return ` AND `;
    default:
      return '';
  }
}

function _extractFilterIds(obj: LogicalComponent | ValueExpression, filterMap: { [id: string]: FilteredVariable }) {
  if (instanceOfValueExpression(obj)) {
    if (!!filterMap[obj.variableId]) {
      // If the variable already exists in the map, add another filter to it
      filterMap[obj.variableId] = {
        variableId: obj.variableId,
        filters: [...filterMap[obj.variableId].filters, { value: obj.value, operation: obj.relationalOperator }],
      };
    } else {
      // If the variable isn't in the map, add it for the first time
      filterMap[obj.variableId] = { variableId: obj.variableId, filters: [{ value: obj.value, operation: obj.relationalOperator }] };
    }
  } else {
    // If it is a LogicalComponent, keep recursing
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop) && prop !== 'logicalOperator') {
        const subObj = obj[prop];
        _extractFilterIds(subObj, filterMap);
      }
    }
  }
}
